import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { resolveImg } from "../lib/api";

export default function Lightbox({ images, index, onClose, onNavigate }) {
  const total = images?.length || 0;

  const next = useCallback(() => {
    if (total === 0) return;
    onNavigate((index + 1) % total);
  }, [index, total, onNavigate]);

  const prev = useCallback(() => {
    if (total === 0) return;
    onNavigate((index - 1 + total) % total);
  }, [index, total, onNavigate]);

  // Keyboard nav + lock body scroll
  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [index, next, prev, onClose]);

  if (index === null || !images?.[index]) return null;
  const img = images[index];

  return (
    <AnimatePresence>
      <motion.div
        data-testid="lightbox"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[120] bg-black/95 backdrop-blur-sm flex items-center justify-center"
        onClick={onClose}
      >
        {/* TOP BAR */}
        <div className="absolute top-0 inset-x-0 px-6 sm:px-10 py-5 flex items-center justify-between z-10 pointer-events-none">
          <p className="font-mono text-[10px] sm:text-xs tracking-[0.4em] text-white/60 pointer-events-auto">
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </p>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            data-testid="lightbox-close"
            className="pointer-events-auto inline-flex items-center gap-2 text-white/70 hover:text-white"
            aria-label="Cerrar"
          >
            <span className="font-mono text-[10px] tracking-[0.4em] hidden sm:inline">CERRAR · ESC</span>
            <X size={22} />
          </button>
        </div>

        {/* PREV */}
        {total > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            data-testid="lightbox-prev"
            aria-label="Anterior"
            className="absolute left-2 sm:left-6 z-10 p-3 sm:p-4 text-white/60 hover:text-white border border-white/10 hover:border-white/40 transition"
          >
            <ChevronLeft size={28} />
          </button>
        )}

        {/* IMAGE */}
        <motion.img
          key={index}
          src={resolveImg(img.url)}
          alt={img.alt || `Foto ${index + 1}`}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
          className="max-w-[92vw] max-h-[82vh] sm:max-w-[88vw] sm:max-h-[80vh] w-auto h-auto object-contain select-none"
          onClick={(e) => e.stopPropagation()}
          draggable={false}
        />

        {/* NEXT */}
        {total > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            data-testid="lightbox-next"
            aria-label="Siguiente"
            className="absolute right-2 sm:right-6 z-10 p-3 sm:p-4 text-white/60 hover:text-white border border-white/10 hover:border-white/40 transition"
          >
            <ChevronRight size={28} />
          </button>
        )}

        {/* BOTTOM CAPTION */}
        {(img.caption || img.technical) && (
          <div
            className="absolute bottom-0 inset-x-0 px-6 sm:px-12 py-5 z-10 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 pointer-events-none"
            onClick={(e) => e.stopPropagation()}
          >
            {img.caption && (
              <p className="font-serif-accent italic text-base sm:text-lg text-white/80 font-light pointer-events-auto">
                {img.caption}
              </p>
            )}
            {img.technical && (
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/45 pointer-events-auto">
                {img.technical}
              </p>
            )}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
